import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { trackTaglineSignup } from "@/lib/taglineTracker";
import { getBrowserId } from "@/lib/funnelTracker";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      // Attribute the signup to whichever tagline the user most recently
      // saw. Supabase fires SIGNED_IN on both fresh signup and subsequent
      // logins; the tracker itself is idempotent (deduped via localStorage)
      // so it only counts the first signup per browser.
      if (event === "SIGNED_IN" && session?.user) {
        trackTaglineSignup();
        // Merge any guest "Start your journey" intake rows so the
        // personalized lesson follows the user post-signup. Fire-and-forget;
        // never blocks the auth flow.
        try {
          void supabase.rpc("claim_onboarding_intake", {
            _browser_id: getBrowserId(),
          });
        } catch {
          /* ignore */
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
